/**
 * EC2 Instance Management Service
 * Handles communication with backend for EC2 instance operations
 */

import { baseApi } from '../API/api';

class EC2InstanceService {
  constructor() {
    this.baseUrl = `${baseApi}/api/ec2`;
  }

  /**
   * Check if an EC2 instance is ready for streaming
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Status information
   */
  async checkInstanceStatus(projectId) {
    try {
      const response = await fetch(`${this.baseUrl}/status/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking instance status:', error);
      throw error;
    }
  }

  /**
   * Start an EC2 instance for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Start operation result
   */
  async startInstance(projectId) {
    try {
      const response = await fetch(`${this.baseUrl}/start/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error starting instance:', error);
      throw error;
    }
  }

  /**
   * Stop an EC2 instance for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Stop operation result
   */
  async stopInstance(projectId) {
    try {
      const response = await fetch(`${this.baseUrl}/stop/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error stopping instance:', error);
      throw error;
    }
  }

  /**
   * Check if instance is ready for streaming (non-blocking)
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Ready status
   */
  async checkInstanceReady(projectId) {
    try {
      const response = await fetch(`${this.baseUrl}/check-ready/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking instance readiness:', error);
      throw error;
    }
  }

  /**
   * Start instance and wait for it to be ready
   * @param {string} projectId - Project ID
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise<Object>} Final result
   */
  async startInstanceAndWait(projectId, onProgress = null) {
    try {
      // First check if instance is already ready
      const initialStatus = await this.checkInstanceReady(projectId);
      
      if (initialStatus.success && initialStatus.ready_for_streaming) {
        if (onProgress) {
          onProgress({
            step: 'ready',
            message: 'Instance is already ready!',
            progress: 100
          });
        }
        return {
          success: true,
          message: 'Instance is already ready',
          ready_for_streaming: true
        };
      }

      // Start the instance
      if (onProgress) {
        onProgress({
          step: 'starting',
          message: 'Starting EC2 instance...',
          progress: 10
        });
      }

      const startResult = await this.startInstance(projectId);
      
      if (!startResult.success) {
        throw new Error(startResult.message || 'Failed to start instance');
      }

      if (onProgress) {
        onProgress({
          step: 'waiting',
          message: 'Waiting for instance to become ready...',
          progress: 50
        });
      }

      // The backend will handle the waiting and return when ready
      return startResult;

    } catch (error) {
      console.error('Error in startInstanceAndWait:', error);
      throw error;
    }
  }

  /**
   * Get instance information for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Instance information
   */
  async getInstanceInfo(projectId) {
    try {
      const status = await this.checkInstanceStatus(projectId);
      return {
        projectId,
        instanceName: status.instance_name,
        instanceId: status.instance_id,
        state: status.status?.state || 'unknown',
        isHealthy: status.status?.is_healthy || false,
        readyForStreaming: status.ready_for_streaming || false,
        statusChecksPassed: status.status?.status_checks_passed || 0
      };
    } catch (error) {
      console.error('Error getting instance info:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const ec2InstanceService = new EC2InstanceService();
export default ec2InstanceService;
